/**
 * Google My Business Notifications API Service
 * Handles real-time notifications for business profile updates
 */

import GMBService from './GMBService';

import logger from '../utils/logger';
class GMBNotificationService {
  constructor() {
    this.accountId = null; // Will be set dynamically
    this.baseUrl = 'https://mybusinessnotifications.googleapis.com/v1';
    this.notificationCache = new Map();
    this.pollingInterval = null;
    this.listeners = new Set();
    this.consecutiveErrors = 0;
    this.maxConsecutiveErrors = 3;
    this.isBackedOff = false;
  }

  /**
   * Set the account ID dynamically based on the current profile
   * @param {string} accountId - The account ID to use
   */
  setAccountId(accountId) {
    logger.log('[GMBNotificationService] Setting account ID to:', accountId);
    this.accountId = accountId;
  }

  /**
   * Auto-detect account ID from location
   * @param {string} accessToken - Google OAuth access token
   * @param {string} locationId - Location ID to find account for
   * @returns {Promise<string>} The account ID that owns this location
   */
  async detectAccountId(accessToken, locationId) {
    try {
      logger.log('[GMBNotificationService] Auto-detecting account ID for location:', locationId);
      
      const accounts = await GMBService.getAccounts(accessToken);
      logger.log('[GMBNotificationService] Available accounts:', accounts.map(a => ({ id: a.name?.split('/').pop(), name: a.accountName })));
      
      // Try to find the location in each account
      for (const account of accounts) {
        try {
          const locations = await GMBService.getLocations(accessToken, account.name);
          const foundLocation = locations.find(loc => loc.name.includes(locationId));
          if (foundLocation) {
            const accountId = account.name.split('/').pop();
            logger.log('[GMBNotificationService] Found location in account:', accountId);
            this.setAccountId(accountId);
            return accountId;
          }
        } catch (e) {
          logger.warn('[GMBNotificationService] Could not check locations for account:', account.name);
        }
      }
      
      throw new Error(`Location ${locationId} not found in any accessible account`);
    } catch (error) {
      logger.error('[GMBNotificationService] Error detecting account ID:', error);
      throw error;
    }
  }

  /**
   * Get notification settings for the account
   * @param {string} accessToken - Google OAuth access token
   * @returns {Promise<Object>} Notification settings
   */
  async getNotificationSettings(accessToken) {
    try {
      if (!this.accountId) {
        throw new Error('Account ID not set. Call detectAccountId() first.');
      }
      
      const url = `${this.baseUrl}/accounts/${this.accountId}/notificationSetting`;
      logger.log('[GMBNotificationService] Fetching notification settings from:', url);
      
      const token = await GMBService.getAccessToken(accessToken);
      logger.log('[GMBNotificationService] Using access token:', token ? 'Token available' : 'No token');
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      logger.log('[GMBNotificationService] Response status:', response.status, response.statusText);
      logger.log('[GMBNotificationService] Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('[GMBNotificationService] Error response body:', errorText);
        let errorData = {};
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          logger.warn('[GMBNotificationService] Could not parse error response as JSON');
        }
        throw new Error(errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      logger.log('[GMBNotificationService] Notification settings response:', data);
      return data;
    } catch (error) {
      logger.error('[GMBNotificationService] Error fetching notification settings:', error);
      throw error;
    }
  }

  /**
   * Update notification settings with Pub/Sub topic
   * @param {string} accessToken - Google OAuth access token
   * @param {string} pubsubTopic - Pub/Sub topic name
   * @returns {Promise<Object>} Updated notification settings
   */
  async updateNotificationSettings(accessToken, pubsubTopic) {
    try {
      if (!this.accountId) {
        throw new Error('Account ID not set. Call detectAccountId() first.');
      }
      
      const url = `${this.baseUrl}/accounts/${this.accountId}/notificationSetting?updateMask=pubsub_topic`;
      
      const requestBody = {
        pubsubTopic: pubsubTopic,
        notificationTypes: [
          'NEW_REVIEW',
          'UPDATED_REVIEW',
          'NEW_CUSTOMER_MEDIA',
          'NEW_QUESTION',
          'UPDATED_QUESTION',
          'NEW_ANSWER',
          'UPDATED_ANSWER',
          'DUPLICATE_LOCATION',
          'LOCATION_VERIFICATION'
        ]
      };

      const token = await GMBService.getAccessToken(accessToken);
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || 'Failed to update notification settings');
      }

      return await response.json();
    } catch (error) {
      logger.error('Error updating notification settings:', error);
      throw error;
    }
  }

  /**
   * Get recent notifications by polling the API
   * @param {string} accessToken - Google OAuth access token
   * @param {string} locationId - Location ID to filter notifications
   * @returns {Promise<Array>} Array of notifications
   */
  async getRecentNotifications(accessToken, locationId) {
    try {
      logger.log('[GMBNotificationService] Getting recent notifications for location:', locationId);
      
      // Auto-detect account ID if not already set
      if (!this.accountId) {
        await this.detectAccountId(accessToken, locationId);
      }
      
      const correctAccountId = this.accountId;
      
      // Since direct notification retrieval might not be available,
      // we'll simulate by checking for recent changes in reviews, media, etc.
      const notifications = [];
      
      // Check for new reviews
      try {
        const accountName = `accounts/${correctAccountId}`;
        const locationName = `locations/${locationId}`;
        logger.log('[GMBNotificationService] Fetching reviews from:', accountName, locationName);
        const reviewsData = await GMBService.getReviews(accessToken, accountName, locationName);
        logger.log('[GMBNotificationService] Reviews data received:', reviewsData);
        
        if (reviewsData?.reviews?.length > 0) {
          // Get recent reviews (last 24 hours)
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          
          const recentReviews = reviewsData.reviews.filter(review => {
            if (!review.createTime) return false;
            const reviewDate = new Date(review.createTime);
            return reviewDate > yesterday;
          });

          logger.log('[GMBNotificationService] Found', recentReviews.length, 'recent reviews');
          recentReviews.forEach(review => {
            const notification = {
              id: `review_${review.name?.split('/').pop() || Date.now()}`,
              type: 'NEW_REVIEW',
              title: 'New review received',
              description: `${review.reviewer?.displayName || 'A customer'} left a ${this.getStarRating(review.starRating)} star review`,
              timestamp: review.createTime,
              data: review,
              icon: review.starRating === 'FIVE' || review.starRating === 'FOUR' ? 'review' : 'review-red'
            };
            logger.log('[GMBNotificationService] Adding review notification:', notification);
            notifications.push(notification);
          });
        }
      } catch (error) {
        logger.warn('Error fetching recent reviews for notifications:', error);
      }

      // Check for new media
      try {
        logger.log('[GMBNotificationService] Fetching media for account/location:', correctAccountId, locationId);
        
        // Try to get media - if entity not found, skip media notifications
        let mediaItems = [];
        try {
          mediaItems = await GMBService.getMedia(accessToken, correctAccountId, locationId);
          logger.log('[GMBNotificationService] Media items received:', mediaItems?.length || 0, 'items');
        } catch (mediaError) {
          if (mediaError.message.includes('not found') || mediaError.message.includes('404')) {
            logger.warn('[GMBNotificationService] Media not found for this account/location combination, skipping media notifications');
            logger.warn('[GMBNotificationService] Account ID:', correctAccountId, 'Location ID:', locationId);
          } else {
            throw mediaError; // Re-throw if it's a different error
          }
        }
        
        if (mediaItems?.length > 0) {
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          
          const recentMedia = mediaItems.filter(item => {
            if (!item.createTime) return false;
            const mediaDate = new Date(item.createTime);
            return mediaDate > yesterday;
          });

          logger.log('[GMBNotificationService] Found', recentMedia.length, 'recent media items');
          recentMedia.forEach(media => {
            const notification = {
              id: `media_${media.name?.split('/').pop() || Date.now()}`,
              type: 'NEW_CUSTOMER_MEDIA',
              title: 'New photo added to your profile',
              description: 'A customer has added a new photo to your business profile',
              timestamp: media.createTime,
              data: media,
              icon: 'photo'
            };
            logger.log('[GMBNotificationService] Adding media notification:', notification);
            notifications.push(notification);
          });
        }
      } catch (error) {
        logger.warn('Error fetching recent media for notifications:', error);
      }

      // Check for new local posts
      try {
        logger.log('[GMBNotificationService] Fetching local posts for account/location:', correctAccountId, locationId);
        
        const localPosts = await GMBService.getLocalPosts(accessToken, correctAccountId, locationId);
        logger.log('[GMBNotificationService] Local posts received:', localPosts?.length || 0, 'posts');
        
        if (localPosts?.length > 0) {
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          
          const recentPosts = localPosts.filter(post => {
            if (!post.createTime) return false;
            const postDate = new Date(post.createTime);
            return postDate > yesterday;
          });

          logger.log('[GMBNotificationService] Found', recentPosts.length, 'recent local posts');
          recentPosts.forEach(post => {
            const notification = {
              id: `post_${post.name?.split('/').pop() || Date.now()}`,
              type: 'NEW_LOCAL_POST',
              title: 'New post published',
              description: post.summary || post.callToAction?.actionType || 'A new post has been published to your business profile',
              timestamp: post.createTime,
              data: post,
              icon: 'post'
            };
            logger.log('[GMBNotificationService] Adding local post notification:', notification);
            notifications.push(notification);
          });
        }
      } catch (error) {
        logger.warn('Error fetching recent local posts for notifications:', error);
      }

      // Sort notifications by timestamp (newest first)
      notifications.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      logger.log('[GMBNotificationService] Total notifications found:', notifications.length);
      const result = notifications.slice(0, 10); // Return last 10 notifications
      logger.log('[GMBNotificationService] Returning', result.length, 'notifications:', result);
      return result;
    } catch (error) {
      logger.error('Error fetching recent notifications:', error);
      return [];
    }
  }

  /**
   * Convert star rating enum to number
   * @param {string} starRating - Star rating enum (e.g., 'FIVE', 'FOUR')
   * @returns {number} Numeric rating
   */
  getStarRating(starRating) {
    const ratingMap = {
      'ONE': 1,
      'TWO': 2,
      'THREE': 3,
      'FOUR': 4,
      'FIVE': 5
    };
    return ratingMap[starRating] || 0;
  }

  /**
   * Start polling for notifications
   * @param {string} accessToken - Google OAuth access token
   * @param {string} locationId - Location ID to monitor
   * @param {number} intervalMs - Polling interval in milliseconds (default: 5 minutes)
   */
  startPolling(accessToken, locationId, intervalMs = 300000) {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }

    // Initial fetch
    this.fetchAndNotify(accessToken, locationId);

    // Set up polling
    this.pollingInterval = setInterval(() => {
      this.fetchAndNotify(accessToken, locationId);
    }, intervalMs);

    logger.log(`Started notification polling for location ${locationId} every ${intervalMs/1000} seconds`);
  }

  /**
   * Stop polling for notifications
   */
  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
      logger.log('Stopped notification polling');
    }
  }

  /**
   * Fetch notifications and notify listeners
   * @param {string} accessToken - Google OAuth access token
   * @param {string} locationId - Location ID
   */
  async fetchAndNotify(accessToken, locationId) {
    try {
      // Check if we're in backoff mode
      if (this.isBackedOff) {
        logger.log('[GMBNotificationService] Skipping poll due to backoff after consecutive errors');
        return;
      }

      logger.log('[GMBNotificationService] Polling for notifications...');
      const notifications = await this.getRecentNotifications(accessToken, locationId);

      // Reset error counter on successful fetch
      this.consecutiveErrors = 0;

      // Check for new notifications
      const newNotifications = notifications.filter(notification => {
        const cacheKey = `${locationId}_${notification.id}`;
        if (this.notificationCache.has(cacheKey)) {
          return false;
        }
        this.notificationCache.set(cacheKey, notification);
        return true;
      });

      if (newNotifications.length > 0) {
        logger.log('[GMBNotificationService] Found', newNotifications.length, 'new notifications, notifying listeners');
        // Notify all listeners
        this.listeners.forEach(listener => {
          try {
            listener(newNotifications, notifications);
          } catch (error) {
            logger.error('[GMBNotificationService] Error in notification listener:', error);
          }
        });
      } else {
        logger.log('[GMBNotificationService] No new notifications found');
      }
    } catch (error) {
      logger.error('Error in fetchAndNotify:', error);

      // Increment error counter
      this.consecutiveErrors++;

      // Check if we should enter backoff mode
      if (this.consecutiveErrors >= this.maxConsecutiveErrors) {
        logger.warn(`[GMBNotificationService] ${this.consecutiveErrors} consecutive errors. Entering backoff mode for 10 minutes.`);
        this.isBackedOff = true;

        // Reset backoff after 10 minutes
        setTimeout(() => {
          logger.log('[GMBNotificationService] Exiting backoff mode, resuming polling');
          this.isBackedOff = false;
          this.consecutiveErrors = 0;
        }, 10 * 60 * 1000); // 10 minutes
      }
    }
  }

  /**
   * Add a notification listener
   * @param {Function} listener - Callback function to handle notifications
   */
  addListener(listener) {
    this.listeners.add(listener);
  }

  /**
   * Remove a notification listener
   * @param {Function} listener - Callback function to remove
   */
  removeListener(listener) {
    this.listeners.delete(listener);
  }

  /**
   * Clear notification cache
   */
  clearCache() {
    this.notificationCache.clear();
  }

  /**
   * Get cached notifications for a location
   * @param {string} locationId - Location ID
   * @returns {Array} Cached notifications
   */
  getCachedNotifications(locationId) {
    const notifications = [];
    for (const [key, notification] of this.notificationCache.entries()) {
      if (key.startsWith(`${locationId}_`)) {
        notifications.push(notification);
      }
    }
    return notifications.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }

  /**
   * Format notification for display
   * @param {Object} notification - Notification object
   * @returns {Object} Formatted notification
   */
  formatNotification(notification) {
    const timeAgo = this.getTimeAgo(notification.timestamp);
    
    return {
      ...notification,
      timeAgo,
      formattedTime: new Date(notification.timestamp).toLocaleString()
    };
  }

  /**
   * Get time ago string
   * @param {string} timestamp - ISO timestamp
   * @returns {string} Time ago string
   */
  getTimeAgo(timestamp) {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  }
}

export default new GMBNotificationService();
